import { cva } from "class-variance-authority";
import React from "react";
import { useSelector } from "react-redux";

import Icon from "../../../../components/Icon";
import Tooltip from "../../../../components/Tooltip";

import { getActiveScreen } from "../../selectors";

type RemoveUserFromGroupButtonProps = {
	userId: string;
	isHrisLocked: boolean;
	handleRemoveEmployee: (userId: string) => void;
};

const removeUserFromGroupButtonStyles = cva("text-center text-neutral-2", {
	variants: {
		state: {
			locked: "cursor-not-allowed",
			unlocked: "cursor-pointer hover:text-red-5",
		},
	},
});

/**
 * Renders a remove icon button that can be clicked to remove a user from a group. If the user is managed by an
 * HRIS integration, the button will be locked and a tooltip will be displayed explaining why the user cannot be removed.
 **/
export const RemoveUserFromGroupButton = ({
	userId,
	isHrisLocked,
	handleRemoveEmployee,
}: RemoveUserFromGroupButtonProps) => {
	// We only lock the remove button on the Members screen
	// HRIS integrations manage members, but there's no current means of managing admins of groups.
	// So if we're on the Admins screen, users still need to be able to remove admins.
	const activeScreen = useSelector(getActiveScreen);
	const isLocked = isHrisLocked && activeScreen === "MEMBERS";

	return isLocked ? (
		<Tooltip message={"This user is managed by your HRIS integration and cannot be manually removed."}>
			<div className={removeUserFromGroupButtonStyles({ state: "locked" })} data-testid="remove-column">
				<Icon type="fa5" name={["far", "times-circle"]} size="lg" />
			</div>
		</Tooltip>
	) : (
		<div
			onClick={() => handleRemoveEmployee(userId)}
			onKeyDown={(e) => {
				if (e.key === "Enter") {
					handleRemoveEmployee(userId);
				}
			}}
			className={removeUserFromGroupButtonStyles({ state: "unlocked" })}
			data-testid="remove-column"
		>
			<Icon type="fa5" name={["far", "times-circle"]} size="lg" />
		</div>
	);
};
